"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";
import type { DayPickerProps } from "react-day-picker";

import { cn } from "@/core/utils";

export type CalendarProps = DayPickerProps & {
  className?: string;
};

// Wrapper sobre react-day-picker v10. Estilos vía classNames con Tailwind,
// alineados a los tokens del template (primary, muted, accent).
export function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      classNames={{
        months: "flex flex-col sm:flex-row gap-4",
        month: "flex flex-col gap-3",
        month_caption: "flex justify-center pt-1 items-center text-sm font-medium",
        caption_label: "text-sm font-medium",
        nav: "flex items-center gap-1 absolute right-3 top-3",
        button_previous:
          "size-7 inline-flex items-center justify-center rounded-md hover:bg-muted text-foreground/70 hover:text-foreground transition-colors",
        button_next:
          "size-7 inline-flex items-center justify-center rounded-md hover:bg-muted text-foreground/70 hover:text-foreground transition-colors",
        month_grid: "w-full border-collapse",
        weekdays: "flex",
        weekday:
          "text-muted-foreground w-9 font-normal text-[0.75rem] uppercase tracking-wide",
        week: "flex w-full mt-1",
        day: cn(
          "relative size-9 text-center text-sm focus-within:relative focus-within:z-20",
          "[&:has([data-selected=true])]:bg-accent/40",
          "[&:has([data-range-start=true])]:rounded-l-md [&:has([data-range-start=true])]:bg-primary/20",
          "[&:has([data-range-end=true])]:rounded-r-md [&:has([data-range-end=true])]:bg-primary/20",
        ),
        day_button: cn(
          "size-9 p-0 font-normal hover:bg-muted rounded-md transition-colors",
          "aria-selected:opacity-100",
          "[&[data-selected=true]]:bg-primary [&[data-selected=true]]:text-primary-foreground [&[data-selected=true]]:hover:bg-primary/90",
        ),
        selected: "[&_button]:bg-primary [&_button]:text-primary-foreground",
        range_start:
          "[&_button]:bg-primary [&_button]:text-primary-foreground rounded-l-md",
        range_end:
          "[&_button]:bg-primary [&_button]:text-primary-foreground rounded-r-md",
        range_middle:
          "[&_button]:bg-accent/40 [&_button]:text-foreground [&_button]:rounded-none",
        today: "[&_button]:font-semibold [&_button]:underline",
        outside: "text-muted-foreground/40",
        disabled: "text-muted-foreground/30 cursor-not-allowed",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation, ...rest }) => {
          if (orientation === "left") return <ChevronLeft className="size-4" {...rest} />;
          return <ChevronRight className="size-4" {...rest} />;
        },
      }}
      {...props}
    />
  );
}
